import { JobStatus } from '../crawler/crawler.enums';
import { CrawlfishJob } from '../crawler/crawler.interface';
import { DomainStatusTableRow } from '../crawler/pages/domainStatus/domainLevelStatus/domainLevelStatus';

export const copyText = (text: string) => {
    navigator.clipboard.writeText(text);
};

// Handle unknown type from .catch() errors
export const getErrorMessage = (error: unknown) => {
    if (error instanceof Error) return error.message;
    return String(error);
};

export const normalizeUrlCase = (url: string): string => {
    if (!url) return url;
    // Lowercase the scheme only (e.g. HTTPS:// -> https://). Host/path case is preserved —
    // backend only requires a lowercase scheme; reported host-case issues surface per row.
    return url.replace(/^([a-z]+:\/\/)/i, (m) => m.toLowerCase());
};

export const validateDomain = (url: string): string | null => {
    if (!url || url.trim().length === 0) return 'URL is required';
    if (/\s/.test(url)) return 'URL cannot contain whitespace';
    if (!/^https?:\/\//i.test(url)) return 'URL must start with http:// or https://';
    if (!/^https?:\/\//.test(url)) return 'URL scheme must be lowercase (http:// or https://)';
    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch {
        return 'Malformed URL';
    }
    const host = parsed.hostname;
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i.test(host)) {
        return 'Invalid hostname format';
    }
    if (!/\.[a-z]{2,}$/i.test(host)) return 'Hostname must end in a valid TLD';
    return null;
};

export const validateTrafficSource = (source: string): string | null => {
    return !source || source.trim().length === 0 ? 'Traffic Source is required' : null;
};

export function __assertIsNode(e: EventTarget | null): asserts e is Node {
    if (!e || !("nodeType" in e)) {
        throw new Error(`Node expected`);
    }
}
export const __capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
};

export const __isStringTrueOrFalse = (val: string): boolean => {
    if (val === 'true' || val === 'false') {
        return true;
    } else {
        return false;
    }
};

export const __assignStatusClass = (status: string): string => {
    switch (status) {
        case (JobStatus.Complete):
            return 'success-status';
        case (JobStatus.Queueing):
        case ('active'):
            return 'in-progress-status';
        case (JobStatus.Canceled):
        case (JobStatus.Failed):
            return 'failed-status';
        default:
            return 'neutral';
    }
};

// ToDo: implement centralized state management (consider Redux)

export const __isInternal = (username: string): boolean => {
    return (
        username === 'amcbride@performline.com' || 
        username === 'dwhite@performline.com'  || 
        username === 'dwhite+superuser@performline.com' || 
        username === 'ogillette@performline.com'
    );
};

/* Sorting */
export const __sortJobTableColumns = (sortColumn: string, sortDirection: number, tableData: CrawlfishJob[]) => {

    switch (sortColumn) {
        case 'name':
        case 'owner':
        case 'status':
            tableData.sort((a: CrawlfishJob, b: CrawlfishJob) => {
                if (sortDirection == 1) {
                    return b[sortColumn].localeCompare(a[sortColumn]);
                } else {
                    return a[sortColumn].localeCompare(b[sortColumn]);
                }
            });
            break;
        case 'domains':
            tableData.sort((a: CrawlfishJob, b: CrawlfishJob) => {
                if (sortDirection == 1) {
                    return a.requested_uris.length - b.requested_uris.length;
                } else {
                    return b.requested_uris.length - a.requested_uris.length;
                }
            });
            break;
        case 'created_at':
            tableData.sort((a: CrawlfishJob, b: CrawlfishJob) => {
                if (sortDirection == 1) {
                    return new Date(a.created_at).valueOf() - new Date(b.created_at).valueOf();
                } else {
                    return new Date(b.created_at).valueOf() - new Date(a.created_at).valueOf();
                }
            });
            break;
        case 'reviewed':
            tableData.sort((a: CrawlfishJob, b: CrawlfishJob) => {
                if (sortDirection == 1) {
                    return (Number(a.reviewed) - Number(b.reviewed));
                } else {
                    return (Number(b.reviewed) - Number(a.reviewed));
                }
            });
            break;
    }
    return tableData;
};

export const __sortDomainLevelStatusTable = (sortColumn: string, sortDirection: number, tableData: DomainStatusTableRow[]) => {
    if (sortColumn === 'domain') {
        tableData.sort((a: DomainStatusTableRow, b: DomainStatusTableRow) => {
            if (sortDirection == 1) {
                return b[sortColumn].localeCompare(a[sortColumn]);
            } else {
                return a[sortColumn].localeCompare(b[sortColumn]);
            }
        });
    }
    if (sortColumn === 'count') {
        tableData.sort((a: DomainStatusTableRow, b: DomainStatusTableRow) => {
            if (sortDirection == 1) {
                return b[sortColumn] > a[sortColumn] ? -1 : 1;
            } else {
                return a[sortColumn] < b[sortColumn] ? 1 : -1;
            }
        });
    }
    if (sortColumn === 'error') {
        tableData.sort((a: DomainStatusTableRow, b: DomainStatusTableRow) => {
            if (sortDirection == 1) {
                return b[sortColumn].length > a[sortColumn].length ? -1 : 1;
            } else {
                return a[sortColumn].length < b[sortColumn].length ? 1 : -1;
            }
        });
    }
    return tableData;
};

export function __getCookie(cname: string): string | undefined {
    const name = cname + '=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');

    for(let i = 0; i <ca.length; i++) {
        let c = ca[i];
        c = c.trim();
        if (c.indexOf(name) == 0) {
            return c.substring(name.length, c.length);
        }
    }
    return '';
}

export function __roundToTwoDecimals(num: number): string {
    return (Math.round(num * 100) / 100).toFixed(2);
}
